<!--
  StoreDesignDetail.vue -- the STORE mount of the design detail page.

  Card: Decoration color + family model (https://trello.com/c/2hcgDM7J)
  Handoff: planning/design-handoffs/handoff_2026-08-23/
           2026-08-23-decoration-family-model/
  ADR 0070 "Nest, don't converge and don't diverge".

  URL: /distributor/stores/:id/edit/decorations/artwork/:artwork_id/design

  What this component is, and is not
  ==================================
  It is a CONTEXT RESOLVER and nothing else. ADR 0070 puts ONE design
  page under two routes. The Library route mounts DesignDetailPage
  directly, because everything that page needs is in the artwork id.
  The store route needs one fact the URL does not carry: which client
  this store belongs to.

  So this component fetches that fact and renders the SAME
  DesignDetailPage beneath it. It is NOT a second design page and must
  never become one -- that is ADR 0070's rejected "diverge" option, and
  it is the failure that would look most correct on the day it shipped
  and drift quietly for a year afterwards. If something belongs on the
  design page, it belongs on DesignDetailPage, on BOTH mounts.

  Why a wrapper rather than a beforeEnter or a route prop function
  ===============================================================
  The client id needs a network round trip. A `props` function on the
  route is synchronous, and a `beforeEnter` that fetched would block
  navigation on a request whose failure has no good place to render --
  the user would sit on the previous page while nothing happened. A
  wrapper gets a loading state, an error state and a retry for free,
  and keeps the route table declarative.

  Where the client comes from, with evidence
  ==========================================
  `GET /campaign/getCampaignById/<id>` returns `client_id`: it is in
  the SELECT at api/src/endpoints/campaign.py:1152, whose comment calls
  it load-bearing precisely because an earlier omission starved the
  store editor's own read-back path. This is the same loader the store
  editor uses (StoreAddEdit.vue:2699, read at :2718).

  A store has AT MOST ONE client. `stores.client_id` is a single
  nullable FK (api/src/models/store.py:804) and `Client.stores` is the
  one-to-many other side (api/src/models/client.py:238). ADR 0070's
  "one approval column" premise rests entirely on that cardinality, so
  it is stated here rather than assumed by a reader.

  The failure mode this component exists to prevent
  =================================================
  If the store cannot be resolved, this does NOT fall through to an
  unfiltered design page. An unfiltered page inside a store shell is
  the ADR's rejected "converge" option arrived at by accident: chrome
  that says "one client" wrapped around content that answers for all of
  them. Better a stated error than a page that is quietly answering a
  different question than the one its heading asks.
-->
<template>
    <div class="sdd">
        <div
            v-if="loading"
            class="sdd__msg"
            data-testid="store-design-loading"
        >Loading store...</div>

        <div
            v-else-if="error"
            class="sdd__msg sdd__msg--err"
            data-testid="store-design-error"
        >
            <h2 class="sdd__errtitle">Could not load this store</h2>
            <p class="sdd__errbody">
                The design cannot be shown for one client until we know
                which client this store belongs to.
            </p>
            <button
                type="button"
                class="sdd__retry"
                data-testid="store-design-retry"
                @click="loadStore"
            >Try again</button>
        </div>

        <!--
          The one design page, mounted a second time. Every prop below
          is CONTEXT -- who is asking and on whose behalf. None of it
          changes what the design is.
        -->
        <DesignDetailPage
            v-else
            :artwork_id="artwork_id"
            :store-id="id"
            :store-client-id="clientId"
            :store-name="storeName"
            :store-client-name="clientName"
        />
    </div>
</template>

<script>
import api from "@/services/api";

import DesignDetailPage
    from "@/pages/distributor/decoration-library/detail/family/DesignDetailPage.vue";

export default {
    name: "StoreDesignDetail",
    components: { DesignDetailPage },
    props: {
        /** Store route param, so a string. */
        id: {
            type: [String, Number],
            required: true,
        },
        /** Artwork route param, passed straight through. */
        artwork_id: {
            type: [String, Number],
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            error: false,
            // Null is a real answer here -- a store with no client
            // attached. It is NOT the same as "not loaded yet", which
            // is what `loading` carries.
            clientId: null,
            clientName: "",
            storeName: "",
        };
    },
    watch: {
        id: {
            immediate: true,
            handler() {
                this.loadStore();
            },
        },
    },
    methods: {
        async loadStore() {
            this.loading = true;
            this.error = false;
            try {
                const { data } = await api.get(
                    `/campaign/getCampaignById/${this.id}`
                );
                // A store has at most one client (see the header). The
                // id is the only value this page strictly needs; the
                // name is display chrome and degrades to a generic
                // sentence without it.
                this.clientId = data?.client_id ?? null;
                this.clientName = this.resolveClientName(data);
                this.storeName = data?.name ?? "";
            } catch (e) {
                // Do NOT fall through to an unfiltered page. See the
                // header -- that is the accidental "converge".
                this.error = true;
                this.clientId = null;
                this.clientName = "";
                this.storeName = "";
            } finally {
                this.loading = false;
            }
        },
        /**
         * The client's display name, if the loader happens to carry
         * it.
         *
         * `getCampaignById` selects `client_id` but not the client's
         * name, so this usually resolves to "" and the scope note
         * falls back to naming the store instead of the client. That
         * is a smaller lie than fetching the client separately and
         * risking a name that disagrees with the id -- and it is
         * strictly better than inventing one. The company_name
         * fallback is the store's own, used only when the payload
         * genuinely offers no client name.
         */
        resolveClientName(data) {
            if (!data || data.client_id == null) return "";
            return (
                data.client_name ||
                data.client?.name ||
                data.company_name ||
                ""
            );
        },
    },
};
</script>

<style scoped>
.sdd {
    min-height: 200px;
}
.sdd__msg {
    padding: 28px 22px;
    background: var(--tf-surface-alt);
    border-radius: var(--tf-radius-md);
    color: var(--tf-fg-3);
    font-size: 13px;
    margin: 20px 26px;
}
.sdd__errtitle {
    margin: 0 0 6px;
    font-size: 17px;
    font-weight: 500;
    color: var(--tf-fg-1);
}
.sdd__errbody {
    margin: 0 0 12px;
    font-size: 13px;
    line-height: 1.5;
}
.sdd__retry {
    background: none;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-sm);
    padding: 6px 13px;
    font: inherit;
    font-size: 12.5px;
    color: var(--tf-primary);
    cursor: pointer;
}
</style>
